<template>
  <yandex-map
    v-if="apiKey"
    :settings="settings"
    :coords="mapCenter"
    :zoom="zoom"
    :controls="[]"
    class="zones-map"
    @map-was-initialized="onMapReady"
  />
</template>

<script>
  import { mapState } from 'vuex'
  import { yandexMap } from 'vue-yandex-maps'
  import { ZONE_COLORS } from '@/constants/deliveryZones'
  import { polygonCenter, polygonAreaSqM, zonesPlural, zonesBounds } from '@/util/geo'

  // исходный вид карты (вся сеть): Москва, зум 11; сюда же возвращаемся с филиала
  const DEFAULT_CENTER = [55.751244, 37.618423]
  const DEFAULT_ZOOM = 11
  // филиал без зон: его точка крупнее
  const FILIAL_ZOOM = 13

  export default {
    name: 'ZonesMap',
    components: { yandexMap },
    props: {
      zones: { type: Array, default: () => [] },
      filials: { type: Array, default: () => [] },
      selectedFilial: { type: [String, Number], default: 'all' },
      selectedId: { type: [String, Number], default: null },
      drawing: { type: Boolean, default: false },
      // нет права edit → read-only: вершины зон не редактируются, клик открывает балун
      canEdit: { type: Boolean, default: true },
    },
    data: () => ({
      myMap: null,
      drawingPoly: null,
      pinsCollection: null,
      // Полигоны зон держим сами (GeoObjectCollection + ymaps.Polygon на id), а не через
      // <ymap-marker>: обёртка vue-yandex-maps пересоздаёт маркер на каждый сменившийся
      // проп и при смене двух пропсов за один тик добавляет его на карту дважды
      // (замер 2026-10-14: 927 зон → 1548 полигонов). Здесь полигон создаётся один раз,
      // дальше меняются только geometry и options.
      zonesCollection: null,
      editPoly: null,
      openZoneId: null,
      // Стартовый центр для <yandex-map>; дальше карту двигаем только императивно
      // (fitToFilial), чтобы правки геометрии зон не дёргали setCenter через пропсы.
      mapCenter: DEFAULT_CENTER,
      // подсветка зон филиала по клику на его пин
      highlightFilial: null,
    }),
    computed: {
      ...mapState({ apiKey: (state) => state.app.yandexMapsYandexMapsApiKey }),
      settings () {
        return {
          apiKey: this.apiKey,
          lang: 'ru_RU',
          coordorder: 'latlong',
          version: '2.1',
        }
      },
      zoom () {
        return DEFAULT_ZOOM
      },
      // выбранная зона выбранного филиала — редактируется императивно (вершины тянутся мышью),
      // её исключаем из декларативного набора, чтобы не было дубля
      editableZoneId () {
        // read-only: вершины не редактируем
        if (!this.canEdit || this.drawing || this.selectedId == null) return null
        const z = this.zones.find((x) => x.id === this.selectedId)
        return z && z.filialId === this.selectedFilial ? this.selectedId : null
      },
      // цвет редактируемой зоны — следим, чтобы перекрасить императивный editPoly
      editableZoneColor () {
        const z = this.zones.find((x) => x.id === this.editableZoneId)
        return z ? z.color : null
      },
      // сигнатура открытой в балуне зоны — следим, чтобы обновить балун при смене цвета/имени
      openZoneSignature () {
        const z = this.zones.find((x) => x.id === this.openZoneId)
        return z ? z.color + '|' + (z.name || '') : ''
      },
      // z-order зон: выбранный филиал выше всех, затем открытые филиалы, затем закрытые;
      // внутри группы большая площадь ниже, чтобы маленькие зоны не прятались под большими.
      // Отдаётся опцией zIndex, поэтому смена порядка не пересоздаёт полигоны.
      zoneZIndex () {
        const ranked = this.zones
          .map((z) => ({ id: z.id, prio: this.prio(z), area: polygonAreaSqM(z.latLngs) }))
          .sort((a, b) => (a.prio !== b.prio ? a.prio - b.prio : b.area - a.area))
        const map = {}
        ranked.forEach((r, i) => {
          map[r.id] = i + 1
        })
        return map
      },
    },
    watch: {
      zones () {
        this.syncZones()
        this.renderPins()
        // филиал выбран раньше, чем пришли зоны (адрес с ?filial=) — подгоняем теперь
        if (this._fitPending) this.fitToFilial()
      },
      filials () {
        this.renderPins()
        this.restyleZones()
      },
      selectedFilial () {
        this.renderPins()
        this.fitToFilial()
        this.restyleZones()
      },
      selectedId () {
        this.restyleZones()
      },
      highlightFilial () {
        this.restyleZones()
      },
      drawing (on) {
        on ? this.startDrawing() : this.cancelDrawing()
      },
      editableZoneId () {
        this.renderEditable()
        // копию редактируемой зоны в коллекции прячем, прежнюю — показываем
        this.restyleZones()
      },
      // смена цвета выбранной зоны → перекрасить императивный editPoly (без пересоздания)
      editableZoneColor (color) {
        if (this.editPoly && color) {
          const hex = this.colorOf(color)
          this.editPoly.options.set({ fillColor: hex, strokeColor: hex })
        }
      },
      // смена цвета/имени зоны открытого балуна → обновить содержимое балуна
      openZoneSignature () {
        if (!this.myMap || this.openZoneId == null) return
        if (!this.myMap.balloon.isOpen || !this.myMap.balloon.isOpen()) return
        const z = this.zones.find((x) => x.id === this.openZoneId)
        if (z) {
          this.myMap.balloon.open(
            polygonCenter(z.latLngs),
            this.zoneBalloonHtml(z),
          )
        }
      },
    },
    created () {
      // id зоны → { poly, coordsSig, styleSig }; не в data, чтобы Vue не оборачивал ymaps-объекты
      this._zonePolys = new Map()
    },
    beforeDestroy () {
      this.cancelDrawing()
      this.cancelEditable()
      if (this.myMap && this.zonesCollection) this.myMap.geoObjects.remove(this.zonesCollection)
      this._zonePolys.clear()
      if (this._geomDebounce) clearTimeout(this._geomDebounce)
      // Clean up global callbacks
      if (window.__faktEditFilial === this._editFilialCallback) {
        delete window.__faktEditFilial
      }
      if (window.__faktCloseBalloon === this._closeBalloonCallback) {
        delete window.__faktCloseBalloon
      }
      if (window.__faktEditZone === this._editZoneCallback) {
        delete window.__faktEditZone
      }
    },
    methods: {
      colorOf (c) {
        return ZONE_COLORS[c] || ZONE_COLORS.green
      },

      // Подгонка вида только при смене филиала и при первой загрузке его зон:
      // филиал → все его зоны целиком в кадре, вся сеть → исходные центр и зум.
      // Намеренно НЕ зависит от правок геометрии: иначе каждый сдвиг вершины срывал бы карту.
      // Без анимации: следом идёт перекраска ~900 полигонов, и анимация стартовала бы
      // только через 1,5 с после выбора (замер 2026-10-15), а мгновенный вид - сразу.
      fitToFilial () {
        if (!this.myMap) return
        if (this.selectedFilial === 'all') {
          this._fitPending = false
          this.myMap.setCenter(DEFAULT_CENTER, DEFAULT_ZOOM)
          return
        }
        // зоны сети ещё не загружены — подгоним, когда придут (watcher zones)
        this._fitPending = this.zones.length === 0
        const bounds = zonesBounds(this.zones.filter((z) => z.filialId === this.selectedFilial))
        if (bounds) {
          this.myMap.setBounds(bounds, { zoomMargin: 40 })
          return
        }
        // филиал без зон — его точка, если координаты заданы
        const f = this.filials.find((x) => x.id === this.selectedFilial)
        if (f && f.lat != null && f.lng != null) {
          this.myMap.setCenter([f.lat, f.lng], FILIAL_ZOOM)
        }
      },

      // canHighlight: zone's filial is open AND (mode 'all' OR it's the selected filial)
      canHighlight (fid) {
        const f = this.filials.find((x) => x.id === fid)
        const isOpen = f ? f.open : true
        return (
          isOpen && (this.selectedFilial === 'all' || fid === this.selectedFilial)
        )
      },

      // z-order статичен (не зависит от подсветки), чтобы клик не пересортировывал
      // маркеры и не пересоздавал их (иначе балун мгновенно закрывается)
      prio (z) {
        if (this.selectedFilial !== 'all' && z.filialId === this.selectedFilial) { return 3 }
        if (z.filialOpen) return 2
        return 1
      },

      opacityOf (z) {
        if (z.filialId === this.highlightFilial) return 0.8
        if (this.selectedFilial === 'all') return z.filialOpen ? 0.5 : 0.11
        if (z.filialId === this.selectedFilial) {
          // выбранная зона чуть менее прозрачная; остальные зоны филиала без изменений
          if (this.selectedId != null && z.id === this.selectedId) return 0.6
          return 0.5
        }
        return 0.11
      },

      fillOf (z) {
        return {
          color: this.colorOf(z.color),
          opacity: this.opacityOf(z),
          enabled: true,
        }
      },

      strokeOf (z) {
        if (z.filialId === this.highlightFilial) { return { color: '#fff', opacity: 1, width: 1.5625 } }
        // выбранная зона — бордер цвета самой зоны
        if (
          this.selectedId != null &&
          z.id === this.selectedId &&
          z.filialId === this.selectedFilial
        ) {
          return { color: this.colorOf(z.color), opacity: 1, width: 2 }
        }
        const op = this.opacityOf(z)
        return {
          color: op >= 0.3 ? '#ffffff' : '#9aa0a6',
          opacity: 1,
          width: 1.25,
        }
      },

      // Zone balloon HTML (declarative balloon-template on ymap-marker)
      zoneBalloonHtml (z) {
        const color = this.colorOf(z.color)
        // неактивные филиалы не входят в общий список filials — берём вложенный в зону
        const f = this.filials.find((x) => x.id === z.filialId) || z.filial
        const fOpen = z.filialOpen
        const fBadge = fOpen
          ? '<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;border:1.5px solid #e91e63;color:#e91e63;font-size:11px;font-weight:700;flex:0 0 22px">Ф</span>'
          : '<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;border:1.5px solid #9aa0a6;color:#9aa0a6;font-size:11px;font-weight:700;flex:0 0 22px">×</span>'
        const city = f ? f.city : ''
        const name = f ? f.name : ''
        const addr = city ? 'г. ' + city + ', ' + name : name

        const timeMin = z.timeMin != null ? z.timeMin : '—'
        // minSum с бека в копейках (как и в ZoneSettingsPanel) — показываем рубли
        const minSum = z.minSum != null ? Math.round(z.minSum / 100) : '—'
        // карандаш: открыть филиал зоны и выделить её для правки вершин (только с правом edit).
        // id зоны отдаём через data-атрибут: он бывает строкой (временные new-N)
        const editBtn = this.canEdit
          ? '<button class="fakt-tip-btn" style="color:#ff9100" title="Редактировать зону" data-id="' +
            String(z.id).replace(/"/g, '') +
            '" onclick="window.__faktEditZone && window.__faktEditZone(this.getAttribute(\'data-id\'))">&#9998;</button>'
          : ''

        const inactiveChip = !fOpen
          ? '<span style="display:inline-flex;align-items:center;gap:5px;background:#fdecea;border-radius:6px;padding:4px 10px;font-size:12px;color:#ed4543">&#9679; Неактивен</span>'
          : ''

        return (
          '<div style="font-family:Roboto,sans-serif;font-size:13px;min-width:200px;padding:14px">' +
          // Header row: edit + close (native ymaps close is hidden)
          '<div style="display:flex;align-items:center;justify-content:flex-end;gap:6px;margin-bottom:6px">' +
          editBtn +
          '<button class="fakt-tip-btn" title="Закрыть" onclick="window.__faktCloseBalloon && window.__faktCloseBalloon()">&#10005;</button>' +
          '</div>' +
          // Zone name row
          '<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">' +
          '<span style="width:12px;height:12px;border-radius:50%;background:' +
          color +
          ';flex:0 0 12px;display:inline-block"></span>' +
          '<span style="font-weight:500">' +
          (z.name || '') +
          '</span>' +
          '</div>' +
          // Filial row
          '<div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;color:rgba(0,0,0,.54)">' +
          fBadge +
          '<span>' +
          addr +
          '</span>' +
          '</div>' +
          // Chips row
          '<div style="display:flex;flex-wrap:wrap;gap:6px">' +
          inactiveChip +
          '<span style="display:inline-flex;align-items:center;gap:5px;background:#f1f3f6;border-radius:6px;padding:4px 10px;font-size:12px;color:rgba(0,0,0,.7)">&#128336; ' +
          timeMin +
          ' мин</span>' +
          '<span style="display:inline-flex;align-items:center;gap:5px;background:#f1f3f6;border-radius:6px;padding:4px 10px;font-size:12px;color:rgba(0,0,0,.7)">&#128722; от ' +
          minSum +
          ' &#8381;</span>' +
          '</div>' +
          '</div>'
        )
      },

      onZoneClick (z) {
        // Notify parent (it selects the zone if it belongs to the selected filial).
        this.$emit('zone-click', z.id)
        // Балун зоны показываем в режиме всей сети, в read-only (нет права edit) и для чужих зон
        // в режиме филиала: их нельзя выбрать для правки, но посмотреть условия нужно.
        // Своя зона в режиме редактирования филиала клик лишь выбирает - без балуна.
        if (this.selectedFilial !== 'all' && this.canEdit && z.filialId === this.selectedFilial) return
        // Open a MAP-LEVEL balloon (not bound to the geoObject) so it survives the
        // marker recreate that selecting the zone triggers in the wrapper.
        if (this.myMap) {
          this.openZoneId = z.id
          this.myMap.balloon.open(
            polygonCenter(z.latLngs),
            this.zoneBalloonHtml(z),
          )
        }
      },

      onMapReady (mapInstance) {
        this.myMap = mapInstance
        // зум справа: слева его закрывает панель «Список объектов»
        this.myMap.controls.add('zoomControl', { position: { right: 10, top: 10 } })
        // Register global edit-filial callback for balloon pencil button
        this._editFilialCallback = (filialId) => {
          this.$emit('edit-filial', filialId)
          // Close the open balloon
          if (this.myMap) this.myMap.balloon.close()
          this._clearHighlight()
        }
        window.__faktEditFilial = this._editFilialCallback
        // Карандаш в балуне зоны: родитель переключит филиал и выделит зону
        this._editZoneCallback = (id) => {
          const z = this.zones.find((x) => String(x.id) === String(id))
          if (this.myMap) this.myMap.balloon.close()
          this._clearHighlight()
          if (z) this.$emit('edit-zone', z.id)
        }
        window.__faktEditZone = this._editZoneCallback
        // Global close-balloon callback for the custom close button (both balloons)
        this._closeBalloonCallback = () => {
          if (this.myMap) this.myMap.balloon.close()
        }
        window.__faktCloseBalloon = this._closeBalloonCallback
        // Listen for balloon close to clear highlights
        this.myMap.events.add('balloonclose', () => {
          this.openZoneId = null
          this._clearHighlight()
        })
        this.syncZones()
        this.renderPins()
        this.renderEditable()
        // страница открыта сразу на филиале (?filial=) — карта стартует с его зон
        if (this.selectedFilial !== 'all') this.fitToFilial()
      },

      // ---- зоны: императивная коллекция (см. комментарий у zonesCollection в data) ----
      zoneStyle (z) {
        const fill = this.fillOf(z)
        const stroke = this.strokeOf(z)
        return {
          fillColor: fill.color,
          fillOpacity: fill.opacity,
          strokeColor: stroke.color,
          strokeOpacity: stroke.opacity,
          strokeWidth: stroke.width,
          zIndex: this.zoneZIndex[z.id] || 0,
          // выбранная зона филиала рисуется отдельным editPoly с редактором — её копию прячем
          visible: z.id !== this.editableZoneId,
        }
      },
      // options.set только при реальной смене стиля: 900+ зон, лишние перерисовки заметны
      applyZoneStyle (entry, z) {
        const style = this.zoneStyle(z)
        const sig = JSON.stringify(style)
        if (entry.styleSig === sig) return
        entry.styleSig = sig
        entry.poly.options.set(style)
      },
      // полная сверка с props.zones: новые — создать, исчезнувшие — убрать,
      // у остальных обновить геометрию (если изменилась) и стиль
      syncZones () {
        if (!this.myMap || !window.ymaps) return
        if (!this.zonesCollection) {
          this.zonesCollection = new window.ymaps.GeoObjectCollection()
          this.myMap.geoObjects.add(this.zonesCollection)
        }
        const alive = new Set()
        this.zones.forEach((z) => {
          alive.add(z.id)
          let entry = this._zonePolys.get(z.id)
          if (!entry) {
            const poly = new window.ymaps.Polygon([z.latLngs], {}, {})
            const id = z.id
            // данные зоны берём на момент клика, а не замыкаем старый объект
            poly.events.add('click', () => {
              const cur = this.zones.find((x) => x.id === id)
              if (cur) this.onZoneClick(cur)
            })
            entry = { poly, coordsSig: null, styleSig: null }
            this._zonePolys.set(id, entry)
            this.zonesCollection.add(poly)
          }
          const coordsSig = JSON.stringify(z.latLngs)
          if (entry.coordsSig !== coordsSig) {
            entry.coordsSig = coordsSig
            entry.poly.geometry.setCoordinates([z.latLngs])
          }
          this.applyZoneStyle(entry, z)
        })
        this._zonePolys.forEach((entry, id) => {
          if (alive.has(id)) return
          this.zonesCollection.remove(entry.poly)
          this._zonePolys.delete(id)
        })
      },
      // только стили (выбор зоны/филиала, подсветка, z-order) — без пересоздания и геометрии
      restyleZones () {
        if (!this.zonesCollection) return
        this.zones.forEach((z) => {
          const entry = this._zonePolys.get(z.id)
          if (entry) this.applyZoneStyle(entry, z)
        })
      },

      _clearHighlight () {
        this.highlightFilial = null
      },

      // Редактирование вершин выбранной зоны: отдельный ymaps.Polygon с editor.startEditing()
      renderEditable () {
        if (!this.myMap || !window.ymaps) return
        this.cancelEditable()
        const id = this.editableZoneId
        if (id == null) return
        const z = this.zones.find((x) => x.id === id)
        if (!z || !z.latLngs || !z.latLngs.length) return

        const poly = new window.ymaps.Polygon(
          [z.latLngs],
          {},
          {
            fillColor: this.colorOf(z.color),
            fillOpacity: 0.6,
            strokeColor: this.colorOf(z.color),
            strokeWidth: 2,
          },
        )
        this.myMap.geoObjects.add(poly)
        poly.editor.startEditing()
        this._editOriginal = JSON.stringify(poly.geometry.getCoordinates())
        poly.geometry.events.add('change', () => this._onEditGeometryChange(id))
        this.editPoly = poly
      },

      cancelEditable () {
        if (this.editPoly) {
          try {
            this.editPoly.editor.stopEditing()
          } catch (e) {}
          this.myMap && this.myMap.geoObjects.remove(this.editPoly)
          this.editPoly = null
        }
      },

      _onEditGeometryChange (id) {
        if (this._geomDebounce) clearTimeout(this._geomDebounce)
        this._geomDebounce = setTimeout(() => {
          if (!this.editPoly) return
          const coords = this.editPoly.geometry.getCoordinates()
          if (JSON.stringify(coords) === this._editOriginal) return // нет реальных изменений
          this._editOriginal = JSON.stringify(coords)
          const ring = (coords && coords[0] ? coords[0] : []).slice()
          if (ring.length < 3) return
          const first = ring[0]
          const last = ring[ring.length - 1]
          if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first)
          // ring — массив [lat,lng] (карта в режиме latlong)
          this.$emit('zone-geometry-changed', { id, points: ring })
        }, 700)
      },

      renderPins () {
        if (!this.myMap || !window.ymaps) return
        if (this.pinsCollection) this.myMap.geoObjects.remove(this.pinsCollection)
        this.pinsCollection = new window.ymaps.GeoObjectCollection()
        // Маркер для каждого филиала: при наличии зон — в центр первой зоны,
        // иначе по координатам филиала. Без зон и без координат — пропускаем.
        this.filials.forEach((f) => {
          const fid = f.id
          const fz = this.zones.filter((x) => x.filialId === fid)
          let coords = null
          if (fz.length) {
            coords = polygonCenter(fz[0].latLngs)
          } else if (f.lat != null && f.lng != null) {
            coords = [f.lat, f.lng]
          }
          if (!coords) return

          const preset = f.open ? 'islands#blackDotIcon' : 'islands#grayDotIcon'
          const filialBalloonHtml = this._filialBalloonHtml(fid, f, fz.length)

          const placemark = new window.ymaps.Placemark(
            coords,
            {
              hintContent: 'г. ' + (f.city || '') + ', ' + f.name,
              balloonContentBody: filialBalloonHtml,
            },
            { preset },
          )

          // When balloon opens, attach click to pencil button
          placemark.events.add('balloonopen', () => {
            const fid_ = fid
            setTimeout(() => {
              const btn = document.getElementById('fakt-edit-filial-' + fid_)
              if (btn) {
                btn.addEventListener('click', () => {
                  if (window.__faktEditFilial) window.__faktEditFilial(fid_)
                })
              }
            }, 50)
          })

          placemark.events.add('click', () => {
            // Highlight the filial's zones (recreates ZONE polygons, not this pin —
            // so the pin's balloon, opened by the same click, stays open).
            if (this.canHighlight(fid)) this.highlightFilial = fid
            this.$emit('pin-click', fid)
          })

          placemark.events.add('balloonclose', () => {
            this._clearHighlight()
          })

          this.pinsCollection.add(placemark)
        })
        this.myMap.geoObjects.add(this.pinsCollection)
      },

      _filialBalloonHtml (fid, f, zc) {
        const esc = (s) =>
          String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
        const fOpen = f ? f.open : true
        const name = esc(f ? f.name : '')
        const address = esc(f ? f.address : '')

        const fIcon = this._filialIconSvg(f)

        const statusChip = fOpen
          ? '<span style="display:inline-flex;align-items:center;gap:5px;background:#f1f9f0;border-radius:6px;padding:4px 10px;font-size:12px;color:#388e3c">&#9679; Активен</span>'
          : '<span style="display:inline-flex;align-items:center;gap:5px;background:#fdecea;border-radius:6px;padding:4px 10px;font-size:12px;color:#ed4543">&#9679; Неактивен</span>'

        const zonesChip =
          '<span style="display:inline-flex;align-items:center;gap:5px;background:#f1f3f6;border-radius:6px;padding:4px 10px;font-size:12px;color:rgba(0,0,0,.7)">&#9651; ' +
          zonesPlural(zc) +
          '</span>'

        return (
          '<div style="font-family:Roboto,sans-serif;font-size:13px;min-width:210px;padding:14px">' +
          // Header row: edit + close (same grey square style, aligned horizontally)
          '<div style="display:flex;align-items:center;justify-content:flex-end;gap:6px;margin-bottom:8px">' +
          '<button id="fakt-edit-filial-' +
          fid +
          '" class="fakt-tip-btn" style="color:#ff9100" title="Настроить филиал">&#9998;</button>' +
          '<button class="fakt-tip-btn" title="Закрыть" onclick="window.__faktCloseBalloon && window.__faktCloseBalloon()">&#10005;</button>' +
          '</div>' +
          // Filial title row: icon (own / franchise / deactivated) + name
          '<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">' +
          fIcon +
          '<span style="font-weight:500">' +
          name +
          '</span>' +
          '</div>' +
          // Address row
          '<div style="margin-bottom:10px;color:rgba(0,0,0,.54);font-size:13px">' +
          address +
          '</div>' +
          // Chips row
          '<div style="display:flex;flex-wrap:wrap;gap:6px">' +
          statusChip +
          zonesChip +
          '</div>' +
          '</div>'
        )
      },

      // Иконка филиала по логике GlobalFilialSelectItem: !status → деактивирован, franchise → франшиза, иначе → свой
      _filialIconSvg (f) {
        const open = f ? f.open : true
        const franchise = f ? f.franchise : false
        const wrap = (inner) =>
          '<span style="display:inline-flex;align-items:center;justify-content:center;color:#c4cdd9;flex:0 0 18px;width:18px">' +
          inner +
          '</span>'

        if (!open) {
          return '<span style="display:inline-flex;align-items:center;justify-content:center;border-radius:5px;border:1px solid #c4cdd9;width:18px;height:18px;font-size:11px;color:#c4cdd9;flex:0 0 18px">&#10005;</span>'
        }
        if (franchise) {
          return wrap(
            '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
              '<rect x="1.30566" y="0.837646" width="16" height="16" rx="4.5" stroke="currentColor"/>' +
              '<path d="M8.36674 4.89873H10.4811C11.2382 4.89873 11.9091 5.05876 12.4939 5.37884C13.0817 5.69892 13.5418 6.14979 13.8742 6.73147C14.2097 7.31007 14.3774 7.99023 14.3774 8.77195C14.3774 9.52905 14.2097 10.1892 13.8742 10.7524C13.5418 11.3156 13.0817 11.7542 12.4939 12.0681C11.9091 12.379 11.2382 12.5344 10.4811 12.5344H8.36674C7.60656 12.5344 6.93256 12.3805 6.34473 12.0727C5.7569 11.7619 5.29525 11.3249 4.95978 10.7617C4.6274 10.1984 4.4612 9.53367 4.4612 8.76733C4.4612 7.98561 4.62894 7.30545 4.9644 6.72685C5.30294 6.14517 5.76613 5.69584 6.35396 5.37884C6.94179 5.05876 7.61272 4.89873 8.36674 4.89873ZM8.36674 6.56989C7.96049 6.56989 7.60195 6.65145 7.2911 6.81456C6.98334 6.9746 6.74328 7.21774 6.57093 7.54397C6.39859 7.8702 6.31241 8.27799 6.31241 8.76733C6.31241 9.23514 6.40012 9.62446 6.57555 9.9353C6.75405 10.2461 6.99719 10.4785 7.30495 10.6324C7.61272 10.7863 7.96665 10.8632 8.36674 10.8632H10.4949C10.8981 10.8632 11.2505 10.7847 11.5521 10.6278C11.8568 10.4708 12.0922 10.2369 12.2584 9.92607C12.4277 9.61523 12.5123 9.23052 12.5123 8.77195C12.5123 8.2826 12.4277 7.87481 12.2584 7.54858C12.0922 7.22235 11.8568 6.97768 11.5521 6.81456C11.2505 6.65145 10.8981 6.56989 10.4949 6.56989H8.36674ZM10.3703 3.71691V13.7254H8.47292V3.71691H10.3703Z" fill="currentColor"/>' +
              '</svg>',
          )
        }
        return wrap(
          '<svg width="11" height="15" viewBox="0 0 11 15" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M5.29213 14.054C1.50789 8.2668 0.805664 7.67256 0.805664 5.54626C0.822383 4.15879 1.35653 2.83413 2.29239 1.85922C3.22825 0.88431 4.49048 0.337646 5.80566 0.337646C7.12084 0.337646 8.38307 0.88431 9.31894 1.85922C10.2548 2.83413 10.7889 4.15879 10.8057 5.54626C10.8057 7.67342 10.1034 8.2668 6.3192 14.0549C6.26154 14.1422 6.1847 14.2136 6.09524 14.2628C6.00578 14.3121 5.90635 14.3377 5.80545 14.3376C5.70455 14.3376 5.60517 14.3117 5.51578 14.2623C5.4264 14.2129 5.34966 14.1415 5.29213 14.054ZM5.80607 7.74503C6.21802 7.74486 6.62068 7.61584 6.96315 7.37426C7.30561 7.13269 7.5725 6.78941 7.73007 6.38783C7.88764 5.98625 7.92883 5.54439 7.84842 5.11811C7.76802 4.69184 7.56962 4.30029 7.27833 3.99295C6.98703 3.68562 6.61591 3.47631 6.21188 3.39147C5.80784 3.30664 5.38904 3.35009 5.00841 3.51634C4.62779 3.68259 4.30242 3.96417 4.07345 4.32549C3.84448 4.6868 3.72219 5.11163 3.72203 5.54626C3.72203 5.83497 3.77594 6.12085 3.88068 6.38757C3.98542 6.65429 4.13895 6.89662 4.33248 7.10073C4.52601 7.30483 4.75576 7.46671 5.0086 7.57712C5.26144 7.68752 5.53243 7.74429 5.80607 7.74418V7.74503Z" fill="currentColor"/>' +
            '</svg>',
        )
      },

      startDrawing () {
        if (!this.myMap || !window.ymaps) return
        this.drawingPoly = new window.ymaps.Polygon(
          [],
          {},
          {
            editorDrawingCursor: 'crosshair',
            fillColor: ZONE_COLORS.green,
            fillOpacity: 0.18,
            strokeColor: '#ff9100',
            strokeWidth: 2.5,
          },
        )
        this.myMap.geoObjects.add(this.drawingPoly)
        this.drawingPoly.editor.startDrawing()
        // Контур замкнут → редактор выходит из режима рисования. Слушатель снимаем в
        // cancelDrawing ДО stopDrawing(): иначе stopDrawing сам порождает statechange,
        // а тот — повторный finishDrawing (рекурсия на сотни вызовов draw-finished).
        this._drawStateHandler = () => {
          if (this.drawingPoly && !this.drawingPoly.editor.state.get('drawing')) {
            this.finishDrawing()
          }
        }
        this.drawingPoly.editor.events.add('statechange', this._drawStateHandler)
      },
      finishDrawing () {
        if (!this.drawingPoly) return
        const rings = this.drawingPoly.geometry.getCoordinates()
        const ring = rings && rings[0] ? rings[0].slice() : []
        if (ring.length >= 3) {
          const first = ring[0]
          const last = ring[ring.length - 1]
          if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first)
          this.$emit('draw-finished', ring)
        }
        this.cancelDrawing()
      },
      cancelDrawing () {
        const poly = this.drawingPoly
        this.drawingPoly = null
        if (poly) {
          try {
            if (this._drawStateHandler) {
              poly.editor.events.remove('statechange', this._drawStateHandler)
            }
            poly.editor.stopDrawing()
          } catch (e) {}
          if (this.myMap) this.myMap.geoObjects.remove(poly)
        }
        this._drawStateHandler = null
      },
    },
  }
</script>

<style scoped>
.zones-map {
  width: 100%;
  height: 100%;
  min-height: 520px;
  border-radius: 12px;
  overflow: hidden;
}
</style>

<!-- Non-scoped: ymaps balloon DOM lives outside Vue scope -->
<style lang="less">
.zones-map {
  .fakt-tip-btn {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #f1f3f6;
    color: rgba(0, 0, 0, 0.54);
    border: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    transition: background 0.15s;
  }
  .fakt-tip-btn:hover {
    background: #e3e6ea;
  }
  /* прячем нативную кнопку закрытия ymaps — у нас своя, в стиле кнопки редактирования */
  [class*='-balloon__close'] {
    display: none;
  }
  [class*='-balloon__layout'] {
    border-radius: 10px;
  }
  [class*='-balloon__content'] {
    border-radius: 10px;
    /* убираем зарезервированный ymaps внутренний отступ (в т.ч. под спрятанный ×);
       внутренние отступы задаёт наш контент (padding: 14px) */
    margin: 0 !important;
    padding: 0 !important;
  }
}
</style>
